import { useCallback, useEffect, useRef, useState } from 'react';

import { isMplPreApprovalOffer } from 'shared/lib';
import { sendEventPreApprovedOfferRedirect } from 'shared/lib/sendGAEvents';
import type { TPreapprovalOfferStep } from 'shared/types/TRankingService';

import { useGetIframeInfoMutation } from 'entities/preApprovals';

import { getOfferBankName } from './preApprovalOfferAdapters';
import type { TPreApprovalControlOffer } from './types';

type Params = {
  isEnabled: boolean;
  onNext: () => void;
  offer: TPreApprovalControlOffer;
  currentStep?: TPreapprovalOfferStep;
};

const POLLING_INTERVAL = 5_000;

export const useIframeStep = (params: Params) => {
  const { isEnabled, currentStep, onNext, offer } = params;
  const [trigger, { data, isError }] = useGetIframeInfoMutation();
  const [isLoading, setIsLoading] = useState(false);

  const hasCompletedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const preApprovalOfferId =
    (isMplPreApprovalOffer(offer) ? offer.preApprovalOfferId : offer.preApprovalOffer?.id) || '';

  const onLinkReceived = useCallback(
    (link?: string | null) => {
      if (link) {
        sendEventPreApprovedOfferRedirect({
          redirectUrl: link,
          bankName: getOfferBankName(offer),
          offerName: offer.productName,
        });

        const windowReference = window.open(undefined, '_blank');

        if (!windowReference) {
          window.location.href = link;
          return;
        }

        windowReference.location.href = link;
      }

      onNext();
    },
    [offer, onNext],
  );

  useEffect(() => {
    if (!isEnabled || currentStep !== 'iframe' || !preApprovalOfferId || hasCompletedRef.current) {
      return;
    }

    let isCancelled = false;
    let activeRequest: ReturnType<typeof trigger> | null = null;

    const clearTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const runPoll = async () => {
      if (isCancelled || hasCompletedRef.current) {
        return;
      }

      setIsLoading(true);

      activeRequest = trigger({ offerId: preApprovalOfferId });

      try {
        const result = await activeRequest.unwrap();

        if (isCancelled) {
          return;
        }

        if (result.status === 'complete') {
          hasCompletedRef.current = true;
          setIsLoading(false);
          onLinkReceived(result.link);
          return;
        }

        timeoutRef.current = setTimeout(() => {
          runPoll();
        }, POLLING_INTERVAL);
      } catch (err) {
        if (isCancelled) {
          return;
        }

        setIsLoading(false);
      }
    };

    runPoll();

    return () => {
      isCancelled = true;
      setIsLoading(false);
      clearTimer();
      activeRequest?.abort?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isEnabled, preApprovalOfferId]);

  return {
    data,
    isLoading,
    isError,
  };
};
